/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { useCallback, useRef } from 'react';
/**
 * Used when you want to perform some action on the DOM when it mounts
 * and cancel the side effects when it unmounts.
 *
 * Much likes a `useEffect` that depends on a DOM element.
 *
 * @param callback Callback when the element is mounted, and the element is
 *                 passed into the function.
 *
 *                 You can return a destructor when the DOM element is unmounted
 *                 or changed to another element.
 *
 * @param deps Array of variables the callback dependents on.
 *
 * @returns An array of [`refCallback`, `elementRef`].
 *          - `refCallback`: Should be called when the element mounts or
 *                           unmounts. You can pass it to a React element's
 *                           `ref` callback.
 *          - `elementRef`: A React ref object to the element for later use.
 */
export function useDOM(callback, deps) {
    const elementRef = useRef(null);
    const destructorRef = useRef();
    const refCallback = useCallback((element) => {
        destructorRef.current?.();
        if (element) {
            destructorRef.current = callback(element);
        }
        elementRef.current = element;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return [refCallback, elementRef];
}
